/**
 * GenerationHistoryList — generic table of past AI generation runs
 * for themes / topics / posts. Row click opens GenerationReviewDialog.
 *
 * Sprint 15 — review-and-apply pattern.
 */

import React from 'react';
import { Box, Stack, Typography, Chip, Alert } from '@mui/material';
import {
  SDataTable,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import type { GenerationRunSummary } from '../../hooks/useGenerationRuns';

const TESTIDS = {
  root: 'generation-history-list',
  rowPrefix: 'generation-history-row-',
} as const;

const STATUS_COLOR: Record<GenerationRunSummary['status'], 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  running: 'info',
  completed: 'warning',   // completed = awaiting review
  reviewed: 'warning',
  applied: 'success',
  discarded: 'default',
  error: 'error',
};

const STATUS_LABEL: Record<GenerationRunSummary['status'], string> = {
  running: 'Running…',
  completed: 'Awaiting review',
  reviewed: 'Partially applied',
  applied: 'Applied',
  discarded: 'Discarded',
  error: 'Error',
};

export interface GenerationHistoryListProps {
  runs: GenerationRunSummary[];
  loading?: boolean;
  onRowClick?: (run: GenerationRunSummary) => void;
  emptyHint?: string;
}

export const GenerationHistoryList: React.FC<GenerationHistoryListProps> = ({
  runs, loading, onRowClick, emptyHint,
}) => {
  const columns = React.useMemo<SDataTableColumnDef<GenerationRunSummary>[]>(() => [
    {
      accessorKey: 'createdAt',
      header: 'Created',
      Cell: ({ cell }) => {
        const value = cell.getValue<Date | undefined>();
        return (
          <Typography variant="body2">
            {value instanceof Date ? value.toLocaleString() : '—'}
          </Typography>
        );
      },
      size: 180,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => {
        const value = cell.getValue<GenerationRunSummary['status']>();
        return (
          <Chip
            size="small"
            color={STATUS_COLOR[value] ?? 'default'}
            label={STATUS_LABEL[value] ?? value}
          />
        );
      },
      size: 160,
    },
    {
      accessorKey: 'generatedItemCount',
      header: 'Generated',
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          {row.original.generatedItemCount ?? row.original.count ?? '—'}
        </Typography>
      ),
      size: 110,
    },
    {
      accessorKey: 'acceptedCount',
      header: 'Applied',
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          {row.original.appliedCount ?? row.original.acceptedCount ?? '—'}
        </Typography>
      ),
      size: 100,
    },
    {
      accessorKey: 'model',
      header: 'Model',
      Cell: ({ cell }) => {
        const value = cell.getValue<unknown>();
        return value ? <Typography variant="caption">{String(value)}</Typography> : '—';
      },
      size: 190,
    },
    {
      accessorKey: 'userHint',
      header: 'Hint',
      Cell: ({ cell }) => {
        const value = cell.getValue<unknown>();
        return value ? <Typography variant="caption">{String(value).slice(0, 80)}</Typography> : '—';
      },
      size: 260,
    },
  ], []);

  if (loading) return <Typography variant="body2">Loading generation history…</Typography>;

  if (runs.length === 0) {
    return (
      <Alert severity="info" data-testid={TESTIDS.root}>
        {emptyHint ?? 'No generation runs yet. Trigger AI generation to populate.'}
      </Alert>
    );
  }

  return (
    <Box data-testid={TESTIDS.root}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {runs.length} generation run{runs.length === 1 ? '' : 's'}
        </Typography>
      </Stack>
      <SDataTable<GenerationRunSummary>
        columns={columns}
        data={runs}
        getRowId={(row) => row.id}
        enableSorting
        enableTopToolbar={false}
        enablePagination
        enableBottomToolbar
        initialState={{
          sorting: [{ id: 'createdAt', desc: true }],
          pagination: { pageIndex: 0, pageSize: 25 },
        }}
        muiTableContainerProps={{
          sx: {
            maxHeight: 'min(560px, calc(100vh - 320px))',
            overflow: 'auto',
          },
        }}
        muiTableProps={{
          size: 'small',
          sx: { minWidth: 1000 },
        }}
        muiTableBodyRowProps={({ row }) => ({
          hover: true,
          'data-testid': `${TESTIDS.rowPrefix}${row.original.id}`,
          onClick: () => onRowClick?.(row.original),
          sx: { cursor: onRowClick ? 'pointer' : 'default' },
        })}
      />
    </Box>
  );
};

export default GenerationHistoryList;
